import React, { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormLabel,
  Stack,
  TextField,
  Typography,
  Zoom,
} from "@mui/material";
import { collection, addDoc } from "firebase/firestore";
import { firestore } from "../firebase";
import theme from "../styles/themes/theme";

const DesignHelpPopover = ({ canvas, open, onClose }) => {
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [formState, setFormState] = useState(false); // Boolean: false (form), true (success)
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    signtype: "",
    size: "",
    thickness: "",
    numbersides: "",
    signtext: "",
    signcolor: "",
    message: "",
  });
  const [errors, setErrors] = useState({
    firstName: false,
    lastName: false,
    email: false,
    phone: false,
  });

  // Validation functions
  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const validatePhone = (phone) => {
    return /^\+?[\d\s-]{7,}$/.test(phone); // Basic phone validation: at least 7 digits, allows +, -, spaces
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
    setErrors((prev) => ({ ...prev, [id]: false })); // Clear error on input change
  };

  // Validate and submit form
  const handleDesignRequest = async () => {
    setIsSendingEmail(true);

    // Validate required fields
    const newErrors = {
      firstName: formData.firstName.length < 2,
      lastName: formData.lastName.length < 2,
      email: !validateEmail(formData.email),
      phone: !validatePhone(formData.phone),
    };
    setErrors(newErrors);

    if (Object.values(newErrors).some((error) => error)) {
      setIsSendingEmail(false);
      return;
    }

    // Generate email content
    const textMessage = `
      <p><strong>First Name:</strong> ${formData.firstName}</p>
      <p><strong>Last Name:</strong> ${formData.lastName}</p>
      <p><strong>Email:</strong> ${formData.email}</p>
      <p><strong>Phone:</strong> ${formData.phone}</p>
      <p><strong>Sign Type:</strong> ${formData.signtype || "Not specified"}</p>
      <p><strong>Size:</strong> ${formData.size || "Not specified"}</p>
      <p><strong>Thickness:</strong> ${
        formData.thickness || "Not specified"
      }</p>
      <p><strong>Sides:</strong> ${formData.numbersides || "Not specified"}</p>
      <p><strong>Sign Text:</strong> ${formData.signtext || "Not specified"}</p>
      <p><strong>Sign Color:</strong> ${
        formData.signcolor || "Not specified"
      }</p>
      <p><strong>Comments:</strong> ${formData.message || "None"}</p>
    `;

    // Generate canvas screenshot
    const dataURL = canvas.toDataURL({ format: "jpeg" });

    // Prepare Firestore data
    const data = {
      to: ["info@alphabetsigns.com", formData.email],
      message: {
        subject: "Sign Design Request",
        text: textMessage,
        html: textMessage,
        attachments: [{ path: dataURL }],
      },
    };

    try {
      const ref = collection(firestore, "sendEmailRequests");
      await addDoc(ref, data);
      setFormState(true);
    } catch (e) {
      console.error("DesignHelpPopover: Error sending email request", e);
      alert("Failed to send design request. Please try again.");
    }

    setIsSendingEmail(false);
  };

  // Reset form for another request
  const handleResetForm = () => {
    setFormState(false);
    setFormData({
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      signtype: "",
      size: "",
      thickness: "",
      numbersides: "",
      signtext: "",
      signcolor: "",
      message: "",
    });
    setErrors({
      firstName: false,
      lastName: false,
      email: false,
      phone: false,
    });
  };

  return (
    <Dialog
      disableScrollLock
      open={open}
      onClose={onClose}
      TransitionComponent={Zoom}
      fullWidth
      maxWidth="sm"
      sx={{
        "& .MuiDialog-paper": {
          margin: {
            xs: theme.spacing(1),
            sm: theme.spacing(2),
            md: theme.spacing(3),
          },
        },
      }}
      aria-labelledby="design-help-dialog-title"
      aria-describedby="design-help-dialog-description"
    >
      <DialogTitle
        id="design-help-dialog-title"
        sx={{
          backgroundColor: "primary.main",
          color: "white",
          fontFamily: "Roboto",
          fontWeight: "bold",
          fontSize: { xs: "1rem", sm: "1.25rem", md: "1.5rem" },
          padding: {
            xs: theme.spacing(1),
            sm: theme.spacing(2),
            md: theme.spacing(3),
          },
          p: {
            xs: theme.spacing(2),
            sm: theme.spacing(3),
          },
        }}
      >
        Get Design Help
      </DialogTitle>
      <DialogContent dividers>
        <Box
          id="dialogContentText"
          sx={{
            padding: {
              xs: theme.spacing(1.5),
              sm: theme.spacing(2),
              md: theme.spacing(3),
            }, // 12px, 16px, 24px
          }}
        >
          {!formState ? (
            <FormControl component="fieldset" sx={{ width: "100%" }}>
              <FormLabel
                sx={{
                  marginBottom: theme.spacing(0.5),
                  color: "text.primary",
                  fontSize: { xs: "0.875rem", sm: "1rem", md: "1.125rem" },
                }}
              >
                Contact Information
              </FormLabel>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={{
                  xs: theme.spacing(1),
                  sm: theme.spacing(2),
                  md: theme.spacing(3),
                }}
                sx={{ marginBottom: theme.spacing(1) }}
              >
                <TextField
                  id="firstName"
                  label="First Name"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  error={errors.firstName}
                  helperText={
                    errors.firstName ? "Name is required (min 2 chars)" : ""
                  }
                  required
                  variant="outlined"
                  size="small"
                  sx={{ flex: 1 }}
                />
                <TextField
                  id="lastName"
                  label="Last Name"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  error={errors.lastName}
                  helperText={
                    errors.lastName ? "Name is required (min 2 chars)" : ""
                  }
                  required
                  variant="outlined"
                  size="small"
                  sx={{ flex: 1 }}
                />
              </Stack>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={{
                  xs: theme.spacing(1),
                  sm: theme.spacing(2),
                  md: theme.spacing(3),
                }}
                sx={{ marginBottom: theme.spacing(1) }}
              >
                <TextField
                  id="email"
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  error={errors.email}
                  helperText={errors.email ? "Valid email is required" : ""}
                  required
                  variant="outlined"
                  size="small"
                  sx={{ flex: 1 }}
                />
                <TextField
                  id="phone"
                  label="Phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleInputChange}
                  error={errors.phone}
                  helperText={
                    errors.phone ? "Valid phone number is required" : ""
                  }
                  required
                  variant="outlined"
                  size="small"
                  sx={{ flex: 1 }}
                />
              </Stack>
              <FormLabel
                sx={{
                  marginBottom: theme.spacing(0.5),
                  color: "text.primary",
                  fontSize: { xs: "0.875rem", sm: "1rem", md: "1.125rem" },
                }}
              >
                Design Information
              </FormLabel>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={{
                  xs: theme.spacing(1),
                  sm: theme.spacing(2),
                  md: theme.spacing(3),
                }}
                sx={{ marginBottom: theme.spacing(1) }}
              >
                <Stack
                  direction="column"
                  spacing={{
                    xs: theme.spacing(0.5),
                    sm: theme.spacing(1),
                    md: theme.spacing(2),
                  }} // 4px, 8px, 16px
                  sx={{ flex: 1 }}
                >
                  <TextField
                    id="signtype"
                    label="Sign Type"
                    value={formData.signtype}
                    onChange={handleInputChange}
                    variant="outlined"
                    size="small"
                    sx={{ width: "100%" }}
                  />
                  <TextField
                    id="size"
                    label="Size"
                    value={formData.size}
                    onChange={handleInputChange}
                    variant="outlined"
                    size="small"
                    sx={{ width: "100%" }}
                  />
                  <TextField
                    id="thickness"
                    label="Thickness"
                    value={formData.thickness}
                    onChange={handleInputChange}
                    variant="outlined"
                    size="small"
                    sx={{ width: "100%" }}
                  />
                  <TextField
                    id="numbersides"
                    label="Sides"
                    value={formData.numbersides}
                    onChange={handleInputChange}
                    variant="outlined"
                    size="small"
                    sx={{ width: "100%" }}
                  />
                </Stack>
                <Stack
                  direction="column"
                  spacing={{
                    xs: theme.spacing(0.5),
                    sm: theme.spacing(1),
                    md: theme.spacing(2),
                  }}
                  sx={{ flex: 1 }}
                >
                  <TextField
                    id="signtext"
                    label="Sign Text"
                    value={formData.signtext}
                    onChange={handleInputChange}
                    variant="outlined"
                    size="small"
                    sx={{ width: "100%" }}
                  />
                  <TextField
                    id="signcolor"
                    label="Sign Colors"
                    value={formData.signcolor}
                    onChange={handleInputChange}
                    variant="outlined"
                    size="small"
                    sx={{ width: "100%" }}
                  />
                  <TextField
                    id="message"
                    label="Comments"
                    value={formData.message}
                    onChange={handleInputChange}
                    multiline
                    minRows={3}
                    maxRows={5}
                    variant="outlined"
                    sx={{ width: "100%" }}
                  />
                </Stack>
              </Stack>
              <Typography
                variant="body2"
                sx={{
                  marginBottom: theme.spacing(1),
                  color: "text.secondary",
                  fontSize: { xs: "0.75rem", sm: "0.875rem", md: "1rem" },
                }}
              >
                Your design will be emailed to our team as a JPEG attachment. A
                professional designer will contact you within one business day.
              </Typography>
              <Button
                variant="contained"
                onClick={handleDesignRequest}
                disabled={isSendingEmail}
                sx={{
                  bgcolor: "primary.main",
                  "&:hover": { bgcolor: "primary.dark" },
                  height: { xs: "2rem", sm: "2.5rem", md: "3rem" },
                }}
              >
                {isSendingEmail ? "Sending..." : "Request Design Help"}
              </Button>
            </FormControl>
          ) : (
            <Box>
              <Typography
                variant="h6"
                sx={{
                  color: "success.main",
                  marginBottom: theme.spacing(1),
                  fontFamily: "Roboto",
                  fontSize: { xs: "1rem", sm: "1.25rem", md: "1.5rem" },
                }}
              >
                Success
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  marginBottom: theme.spacing(1),
                  color: "text.primary",
                  fontSize: { xs: "0.875rem", sm: "1rem", md: "1.125rem" },
                }}
              >
                Your design request has been sent to our team. A professional
                designer will contact you within one business day.
              </Typography>
              <Button
                variant="contained"
                onClick={handleResetForm}
                sx={{
                  bgcolor: "primary.main",
                  "&:hover": { bgcolor: "primary.dark" },
                  height: { xs: "2rem", sm: "2.5rem", md: "3rem" },
                }}
              >
                Send Another Request
              </Button>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          justifyContent: "flex-end",
          padding: {
            xs: theme.spacing(1),
            sm: theme.spacing(2),
            md: theme.spacing(3),
          },
        }}
      >
        <Button
          variant="text"
          onClick={onClose}
          sx={{
            fontSize: { xs: "0.875rem", sm: "1rem", md: "1.125rem" },
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DesignHelpPopover;
